import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { HabitFormModal } from '../components/HabitFormModal';
import { EmptyState } from '../components/EmptyState';
import { SortableList } from '../components/SortableList';
import { SortableRow } from '../components/SortableRow';
import { Plus, Archive as ArchiveIcon, ChevronRight, ListChecks, Flame, Bell } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';
import { calculateStreaks } from '../lib/streaks';
import { HabitReminderDialog } from '../components/HabitReminderDialog';
import { rescheduleHabitReminders } from '../lib/localNotifications';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

export default function Habits() {
  const { user } = useAuth();
  const [habits, setHabits] = useState([]);
  const [allLogs, setAllLogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [editing, setEditing] = useState(null);
  const [reminderHabit, setReminderHabit] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: h }, { data: l }] = await Promise.all([
      supabase.from('habits').select('*').order('sort_order').order('created_at'),
      supabase.from('habit_logs').select('*').order('log_date'),
    ]);
    setHabits(h || []);
    const m = {};
    for (const log of l || []) {
      m[log.habit_id] = m[log.habit_id] || [];
      m[log.habit_id].push(log);
    }
    setAllLogs(m);
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitHabit = async (form) => {
    if (editing?.id) {
      const { data, error } = await supabase.from('habits').update(form).eq('id', editing.id).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setHabits((hs) => hs.map((h) => (h.id === data.id ? data : h)));
      toast.success('Habit updated');
    } else {
      const maxSort = habits.reduce((m, h) => Math.max(m, h.sort_order ?? 0), 0);
      const { data, error } = await supabase.from('habits').insert({ ...form, user_id: user.id, sort_order: maxSort + 1 }).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setHabits((hs) => [...hs, data]);
      toast.success('Habit created');
    }
    setEditing(null);
    rescheduleHabitReminders();
  };

  const toggleArchive = async (habit) => {
    const { data, error } = await supabase.from('habits').update({ archived_at: habit.archived_at ? null : new Date().toISOString() }).eq('id', habit.id).select().maybeSingle();
    if (error) { toast.error(error.message); return; }
    setHabits((hs) => hs.map((h) => (h.id === data.id ? data : h)));
    toast.success(habit.archived_at ? 'Restored' : 'Archived');
    rescheduleHabitReminders();
  };

  const persistOrder = async (orderedIds) => {
    const updates = orderedIds.map((id, idx) => ({ id, sort_order: idx + 1 }));
    setHabits((prev) => {
      const map = new Map(prev.map((h) => [h.id, h]));
      const reordered = orderedIds.map((id, idx) => ({ ...map.get(id), sort_order: idx + 1 }));
      const remaining = prev.filter((h) => !orderedIds.includes(h.id));
      return [...reordered, ...remaining];
    });
    try {
      const { error } = await supabase.from('habits').upsert(updates);
      if (error && error.code) toast.error(error.message);
    } catch (e) {
      console.warn('[mfh] reorder upsert non-fatal:', e?.message);
    }
  };

  const list = habits.filter((h) => (tab === 'active' ? !h.archived_at : !!h.archived_at));

  // eslint-disable-next-line react/no-unstable-nested-components
  const Row = ({ h }) => {
    const I = LucideIcons[toIconKey(h.icon)] || LucideIcons.Sparkles;
    const streak = calculateStreaks(h, allLogs[h.id] || []).current;
    return (
      <Link to={`/habits/${h.id}`} className="flex items-center gap-4 px-3 sm:px-4 py-4 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors" data-testid={`habits-list-item-${h.id}`}>
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${h.color}1F`, color: h.color }}>
          <I className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-medium truncate flex items-center gap-2">
            {h.name}
            {streak > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-secondary"><Flame className="h-3 w-3" />{streak}</span>}
          </div>
          <div className="text-xs text-muted-foreground capitalize">{h.type} · {h.schedule_type.replace('_', ' ')} · {h.time_of_day}</div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setReminderHabit(h); }}
          className={`rounded-full p-1.5 hover:bg-muted ${h.reminder_time ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          aria-label={h.reminder_time ? `Reminder ${h.reminder_time}` : 'Set reminder'}
          data-testid={`habits-list-reminder-${h.id}`}
        >
          <Bell className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); toggleArchive(h); }}
          className="text-xs text-muted-foreground hover:text-foreground rounded-full px-3 py-1 hover:bg-muted"
          data-testid={`habits-list-archive-${h.id}`}
        >
          {h.archived_at ? 'Restore' : 'Archive'}
        </button>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    );
  };

  return (
    <div className="space-y-6" data-testid="habits-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">All habits</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Your library</h1>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="rounded-full" data-testid="habits-new-btn">
          <Plus className="h-4 w-4" /> New habit
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="habits-tabs">
          <TabsTrigger value="active" data-testid="habits-tab-active">Active</TabsTrigger>
          <TabsTrigger value="archived" data-testid="habits-tab-archived"><ArchiveIcon className="h-3.5 w-3.5 mr-1" />Archived</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-5">
          {loading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-16 w-full rounded-xl" />)}</div>
          ) : list.length === 0 ? (
            <EmptyState
              icon={ListChecks}
              title={tab === 'active' ? 'No active habits' : 'Nothing archived'}
              description={tab === 'active' ? 'Create your first habit to begin.' : 'Archived habits will appear here. Their history is preserved.'}
              action={tab === 'active' && (
                <Button onClick={() => { setEditing(null); setShowForm(true); }} className="rounded-full" data-testid="habits-empty-new-btn">
                  <Plus className="h-4 w-4" /> Create habit
                </Button>
              )}
              testid="habits-empty"
            />
          ) : (
            <div className="rounded-2xl border border-border bg-card/30 overflow-hidden">
              {tab === 'active' ? (
                <SortableList items={list.map((h) => h.id)} onReorder={persistOrder}>
                  {list.map((h) => (
                    <SortableRow key={h.id} id={h.id} testid={`habits-sortable-${h.id}`}>
                      <Row h={h} />
                    </SortableRow>
                  ))}
                </SortableList>
              ) : (
                list.map((h) => <Row key={h.id} h={h} />)
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <HabitFormModal open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }} initial={editing} onSubmit={submitHabit} />
      <HabitReminderDialog habit={reminderHabit} open={!!reminderHabit} onOpenChange={(o) => !o && setReminderHabit(null)} />
    </div>
  );
}

